/** Quest passthrough, raw controller input and optional live camera screens. */

import { PerspectiveCamera, Scene, WebGLRenderer } from "three";

import { XR_REFERENCE_SPACE, buildXrSnapshot, xrProfilesOf } from "./sample";
import type { XrData } from "./types";
import { createXrVideoPanels, type XrVideoSource, type XrVideoDiagnostics } from "./video-panels";

type InputSessionMode = "immersive-ar" | "immersive-vr";
const SESSION_MODE: InputSessionMode = "immersive-ar";
export const DEFAULT_XR_RATE_HZ = 30;

export async function isXrInputSupported(mode: InputSessionMode = SESSION_MODE): Promise<boolean> {
  if (!navigator.xr) return false;
  try {
    return await navigator.xr.isSessionSupported(mode);
  } catch {
    return false;
  }
}

export interface XrInputControllerOptions {
  onSample: (data: XrData) => void;
  onProfiles?: (profiles: string[]) => void;
  onStatus?: (text: string) => void;
  onActiveChange?: (active: boolean) => void;
  rateHz?: number;
  mode?: InputSessionMode;
  getVideoSources?: () => XrVideoSource[];
  onVideoDiagnostics?: (status: XrVideoDiagnostics[]) => void;
  arrangeWindows?: boolean;
  videoLayoutKey?: string;
}

export interface XrInputController {
  readonly active: boolean;
  start(opts: XrInputControllerOptions): Promise<void>;
  stop(): Promise<void>;
}

export function createXrInputController(): XrInputController {
  let session: XRSession | null = null;
  let opts: XrInputControllerOptions | null = null;
  let cleanup: (() => void) | null = null;

  const onSessionEnd = () => {
    cleanup?.();
    cleanup = null;
    session = null;
    const old = opts;
    opts = null;
    old?.onActiveChange?.(false);
    old?.onStatus?.("VR-сессия завершена");
  };

  return {
    get active() {
      return session != null;
    },

    async start(options) {
      if (session) return;
      if (!navigator.xr) throw new Error("WebXR недоступен в этом браузере");
      opts = options;
      const periodMs = 1000 / Math.max(1, options.rateHz ?? DEFAULT_XR_RATE_HZ);
      const xrSession = await navigator.xr.requestSession(options.mode ?? SESSION_MODE, {
        requiredFeatures: [XR_REFERENCE_SPACE],
      });
      session = xrSession;
      xrSession.addEventListener("end", onSessionEnd);
      const reportProfiles = () =>
        options.onProfiles?.(xrProfilesOf(Array.from(xrSession.inputSources)));
      xrSession.addEventListener("inputsourceschange", reportProfiles);

      try {
        const renderer = new WebGLRenderer({ alpha: true, antialias: true });
        // Keep the XR WebGL canvas in the document, as in controller-state.
        renderer.domElement.style.position = "fixed";
        renderer.domElement.style.inset = "0";
        renderer.domElement.style.width = "100vw";
        renderer.domElement.style.height = "100vh";
        renderer.domElement.style.zIndex = "-1";
        renderer.domElement.style.pointerEvents = "none";
        document.body.appendChild(renderer.domElement);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(1024, 1024);
        // An alpha-zero clear lets Quest show real-world passthrough behind an
        // immersive-ar session. The canvas stays behind the page in panel mode.
        renderer.setClearColor(0x000000, 0);
        renderer.xr.enabled = true;
        renderer.xr.setReferenceSpaceType(XR_REFERENCE_SPACE);
        const scene = new Scene();
        const camera = new PerspectiveCamera(70, 1, .01, 20);
        const videoPanels = options.getVideoSources ? createXrVideoPanels(options.getVideoSources, {
          editable: options.arrangeWindows, storageKey: options.videoLayoutKey,
        }) : null;
        if (videoPanels) scene.add(videoPanels.group);

        cleanup = () => {
          renderer.setAnimationLoop(null);
          videoPanels?.dispose();
          renderer.dispose();
          renderer.domElement.remove();
        };

        await renderer.xr.setSession(xrSession as Parameters<typeof renderer.xr.setSession>[0]);
        const refSpace = await xrSession.requestReferenceSpace(XR_REFERENCE_SPACE);
        let lastSentAt = -Infinity;
        let lastDiagnosticsAt = -Infinity;
        renderer.setAnimationLoop((t, rawFrame) => {
          if (session !== xrSession || !rawFrame) return;
          // Sample before GPU work so drawing three screens doesn't delay input.
          if (t - lastSentAt >= periodMs) {
            lastSentAt = t;
            options.onSample(buildXrSnapshot(t, rawFrame as XRFrame, refSpace));
          }
          videoPanels?.update(performance.now(), (rawFrame as XRFrame).getViewerPose(refSpace),
            rawFrame as XRFrame, refSpace);
          if (videoPanels && options.onVideoDiagnostics && t - lastDiagnosticsAt >= 500) {
            lastDiagnosticsAt = t;
            options.onVideoDiagnostics(videoPanels.diagnostics());
          }
          renderer.render(scene, camera);
        });
      } catch (err) {
        await xrSession.end().catch(() => {});
        onSessionEnd();
        throw err;
      }

      reportProfiles();
      options.onActiveChange?.(true);
      options.onStatus?.(options.getVideoSources
        ? "Quest: VR-ввод и камеры в шлеме активны"
        : "Quest: VR-ввод активен; камеры остаются на компьютере");
    },

    async stop() {
      if (!session) return;
      const old = session;
      try {
        await old.end();
      } catch {
        onSessionEnd();
      }
    },
  };
}
